import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useAuthUser } from "blitzware-react-sdk";
import { Card } from "../ui/card";
import { MapPin, Building, Car, CheckCircle, ArrowRight } from "lucide-react";
import Loader from "../ui/loader";
import { Input } from "../ui/input";

const UserOnboarding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authUser = useAuthUser();

  const [selectedBuilding, setSelectedBuilding] = useState("");
  const [selectedFloor, setSelectedFloor] = useState("");
  const [selectedParkingSpot, setSelectedParkingSpot] = useState("");
  const [apartmentNumber, setApartmentNumber] = useState("");
  const [parkingSpots, setParkingSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Available buildings (for now only Ensor II)
  const buildings = [
    {
      id: "ensor-ii",
      name: t("onboarding.building.ensorII"),
      description: t("onboarding.building.description"),
      icon: Building,
    },
  ];

  useEffect(() => {
    const loadParkingSpots = async () => {
      try {
        setLoading(true);

        // Create API instance with auth headers
        const token = localStorage.getItem("access_token");
        const apiInstance = axios.create({
          baseURL: process.env.REACT_APP_BACKEND_URL + "api/parking",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        const response = await apiInstance.get("/spots");
        const allSpots = response.data.spots || response.data;
        // Filter only spots that don't have an owner
        const availableSpots = allSpots.filter((spot) => !spot.owner_id);
        setParkingSpots(availableSpots);
      } catch (error) {
        console.error("Error loading parking spots:", error);
        setError(t("onboarding.error.failedToLoad"));
      } finally {
        setLoading(false);
      }
    };

    loadParkingSpots();
  }, [t]); // Only depend on translation function

  // Get available floors from parking spots
  const getAvailableFloors = () => {
    const floors = [...new Set(parkingSpots.map((spot) => spot.floor))].sort(
      (a, b) => a - b
    );
    return floors;
  };

  // Get parking spots for selected floor
  const getParkingSpotsForFloor = (floor) => {
    return parkingSpots.filter((spot) => spot.floor === floor);
  };

  // Reset selected parking spot when floor changes
  useEffect(() => {
    setSelectedParkingSpot("");
  }, [selectedFloor]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !selectedBuilding ||
      (!hasNoParkingSpot && (!selectedFloor || !selectedParkingSpot)) ||
      !apartmentNumber.trim()
    ) {
      setError(t("onboarding.error.selectAll"));
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      console.log("UserOnboarding - user data from auth:", authUser);

      if (!authUser || !authUser.id || !authUser.email || !authUser.username) {
        throw new Error("No user data available. Please try logging in again.");
      }

      // Make API call to update user's building and parking spot
      const onboardingData = {
        buildingId: selectedBuilding,
        parkingSpotId: hasNoParkingSpot ? null : selectedParkingSpot,
        userId: authUser.id, // This is actually the external auth ID
        externalAuthId: authUser.id, // Same as userId for clarity
        email: authUser.email,
        username: authUser.username,
        apartmentNumber: apartmentNumber.trim(),
      };
      console.log("UserOnboarding - sending onboarding data:", onboardingData);

      // Create API instance with auth headers
      const token = localStorage.getItem("access_token");
      const userApiInstance = axios.create({
        baseURL: process.env.REACT_APP_BACKEND_URL + "api/users",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      await userApiInstance.post("/onboarding", onboardingData);

      // Redirect to request submitted page
      navigate("/request-submitted");
    } catch (error) {
      console.error("Error updating user preferences:", error);
      setError(t("onboarding.error.failedToUpdate"));
    } finally {
      setSubmitting(false);
    }
  };

  const hasNoParkingSpot = selectedFloor === "no-parking";
  const canProceed =
    selectedBuilding &&
    apartmentNumber.trim() !== "" &&
    (hasNoParkingSpot || (selectedFloor && selectedParkingSpot));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className=" bg-gradient-to-br overflow-y-auto from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-800 h-screen">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-4">
            <span className="text-white text-2xl font-bold">E</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t("onboarding.title")}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t("onboarding.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Selection Forms */}
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-primary" />
                {t("onboarding.building.title")}
              </h2>

              <div className="space-y-3">
                {buildings.map((building) => (
                  <button
                    key={building.id}
                    onClick={() => setSelectedBuilding(building.id)}
                    className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                      selectedBuilding === building.id
                        ? "border-primary bg-primary/5 dark:bg-primary/10"
                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`p-2 rounded-lg ${
                          selectedBuilding === building.id
                            ? "bg-primary text-white"
                            : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                        }`}
                      >
                        <building.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {building.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {building.description}
                        </p>
                      </div>
                      {selectedBuilding === building.id && (
                        <CheckCircle className="w-5 h-5 text-primary ml-auto" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Car className="w-5 h-5 text-primary" />
                {t("onboarding.parking.title")}
              </h2>

              {/* No Parking Option and Floor Selector */}
              {selectedBuilding && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("onboarding.parking.selectOption")}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        if (selectedFloor === "no-parking") {
                          // If already selected "no parking", clicking again deselects it
                          setSelectedFloor("");
                        } else {
                          setSelectedFloor("no-parking");
                          setSelectedParkingSpot("");
                        }
                      }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedFloor === "no-parking"
                          ? "bg-primary text-white"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                      }`}
                    >
                      {t("onboarding.parking.noParkingSpot")}
                    </button>
                    {getAvailableFloors().map((floor) => (
                      <button
                        key={floor}
                        onClick={() => setSelectedFloor(floor)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          selectedFloor === floor
                            ? "bg-primary text-white"
                            : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                        }`}
                      >
                        {t("onboarding.parking.floor")} {floor}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedFloor ? (
                hasNoParkingSpot ? (
                  // Show message when no parking is selected
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Car className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{t("onboarding.parking.noParkingSelected")}</p>
                  </div>
                ) : getParkingSpotsForFloor(selectedFloor).length > 0 ? (
                  // Show available parking spots list
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {getParkingSpotsForFloor(selectedFloor).map((spot) => (
                      <button
                        key={spot.id}
                        onClick={() => setSelectedParkingSpot(spot.id)}
                        className={`w-full p-3 border-2 rounded-lg text-left transition-all ${
                          selectedParkingSpot === spot.id
                            ? "border-primary bg-primary/5 dark:bg-primary/10"
                            : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={`p-2 rounded-lg ${
                                selectedParkingSpot === spot.id
                                  ? "bg-primary text-white"
                                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                              }`}
                            >
                              <MapPin className="w-4 h-4" />
                            </div>
                            <div>
                              <span className="font-medium text-gray-900 dark:text-white">
                                Spot {spot.spot_number}
                              </span>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Floor {spot.floor} • {spot.type}
                              </p>
                            </div>
                          </div>
                          {selectedParkingSpot === spot.id && (
                            <CheckCircle className="w-5 h-5 text-primary" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  // Show no spots available message when floor is selected but no spots
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Car className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{t("onboarding.parking.noSpotsAvailable")}</p>
                  </div>
                )
              ) : (
                // Show select floor message when no floor is selected
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Car className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>{t("onboarding.parking.selectFloor")}</p>
                </div>
              )}
            </Card>

            {/* Appartements nummer ingeven (mag tekst zijn, niet leeg ) */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                {t("onboarding.apartmentNumber.label")}
              </h2>

              <Input
                value={apartmentNumber}
                onChange={(e) => setApartmentNumber(e.target.value)}
                placeholder={t("onboarding.apartmentNumber.placeholder")}
                className={`mb-4 ${
                  !apartmentNumber.trim()
                    ? "border-red-500 dark:border-red-400"
                    : ""
                }`}
                required
              />
              {!apartmentNumber.trim() && (
                <p className="text-red-500 dark:text-red-400 text-sm mb-2">
                  {t("onboarding.error.apartmentNumberRequired")}
                </p>
              )}
            </Card>
            {/* Submit Button */}
            <Card className="p-6">
              <button
                onClick={handleSubmit}
                disabled={!canProceed || submitting}
                className={`w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-medium transition-all ${
                  canProceed
                    ? "bg-primary hover:bg-primary/90 text-white"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                }`}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    {t("onboarding.submitting")}
                  </>
                ) : (
                  <>
                    {t("onboarding.submit")}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              {error && (
                <p className="text-red-500 text-sm text-center mt-3">{error}</p>
              )}
            </Card>
          </div>

          {/* Right Column - Parking Grid Visualization */}
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">
                {t("onboarding.layout.title")}
              </h2>

              {selectedBuilding && selectedFloor && (
                <div className="space-y-4">
                  <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
                    <h3 className="font-medium mb-2">
                      {t("onboarding.layout.floorOverview")} -{" "}
                      {t("onboarding.parking.floor")} {selectedFloor}
                    </h3>

                    {/* Dynamic parking grid based on column_number and row_number */}
                    {(() => {
                      // Get all available spots for the selected building and floor
                      const buildingSpots =
                        getParkingSpotsForFloor(selectedFloor);

                      if (buildingSpots.length === 0) {
                        return (
                          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            <p>{t("onboarding.parking.noSpotsAvailable")}</p>
                          </div>
                        );
                      }

                      // Find the maximum column and row numbers
                      const maxColumn = Math.max(
                        ...buildingSpots.map((spot) => spot.column_number || 1)
                      );
                      const maxRow = Math.max(
                        ...buildingSpots.map((spot) => spot.row_number || 1)
                      );

                      // Create a grid with the correct dimensions
                      const gridCols = maxColumn;

                      return (
                        <div className="space-y-4">
                          {/* Grid container */}
                          <div
                            className="grid gap-1 overflow-y-auto"
                            style={{
                              gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
                              maxHeight: "400px",
                              scrollbarWidth: "thin",
                              scrollbarColor: "rgb(156 163 175) transparent",
                            }}
                          >
                            {Array.from({ length: maxRow }, (_, rowIndex) =>
                              Array.from(
                                { length: maxColumn },
                                (_, colIndex) => {
                                  const row = rowIndex + 1;
                                  const col = colIndex + 1;

                                  // Find the spot at this position
                                  const spot = buildingSpots.find(
                                    (s) =>
                                      (s.row_number === row ||
                                        s.row_number === rowIndex + 1) &&
                                      (s.column_number === col ||
                                        s.column_number === colIndex + 1)
                                  );

                                  if (!spot) {
                                    // Empty space
                                    return (
                                      <div
                                        key={`empty-${row}-${col}`}
                                        className="aspect-square rounded border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800"
                                      />
                                    );
                                  }

                                  return (
                                    <div
                                      key={spot.id}
                                      className={`aspect-square rounded border-2 flex items-center justify-center text-xs font-medium transition-all cursor-pointer ${
                                        selectedParkingSpot === spot.id
                                          ? "bg-primary border-primary text-white"
                                          : "bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-800"
                                      }`}
                                      onClick={() =>
                                        setSelectedParkingSpot(spot.id)
                                      }
                                      title={`Spot ${spot.spot_number} - Row ${spot.row_number}, Column ${spot.column_number}`}
                                    >
                                      {spot.spot_number}
                                    </div>
                                  );
                                }
                              )
                            )}
                          </div>

                          {/* Legend */}
                          <div className="flex items-center gap-4 text-xs">
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
                              <span>{t("onboarding.layout.available")}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 bg-primary border border-primary rounded"></div>
                              <span>{t("onboarding.layout.selected")}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 bg-gray-50 border border-gray-300 rounded"></div>
                              <span>{t("onboarding.layout.empty")}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {selectedParkingSpot && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                        {t("onboarding.layout.selectedSpotDetails")}
                      </h4>
                      <div className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
                        <p>
                          {t("onboarding.layout.spotNumber")}:{" "}
                          {
                            parkingSpots.find(
                              (s) => s.id === selectedParkingSpot
                            )?.spot_number
                          }
                        </p>
                        <p>
                          {t("onboarding.layout.floor")}:{" "}
                          {
                            parkingSpots.find(
                              (s) => s.id === selectedParkingSpot
                            )?.floor
                          }
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserOnboarding;
